import { faCrown, faKey, faTrashCan, faUserPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FormEvent, useCallback, useEffect, useState } from "react";
import { CloseButton } from "../components/CloseButton";
import { SideBar } from "../components/SideBar";
import { useAccount } from "../state";

interface Member {
  username: string;
  firstName: string;
  lastName: string;
}
interface PendingInvite {
  id: number;
  email: string;
}
interface Organization {
  id: number;
  name: string;
  owner: string;
  isOwner: boolean;
  members: Member[];
  invites: PendingInvite[];
}
interface Account {
  user: { username: string; firstName: string; lastName: string; email: string };
  organizations: Organization[];
}

const memberName = (m: Member) => (m.firstName ? `${m.firstName} ${m.lastName}` : m.username);

const InviteForm = ({ org, onInvited }: { org: Organization; onInvited: () => void }) => {
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!email.trim() || busy) return;
    setBusy(true);
    setMessage(null);
    const response = await fetch(`/api/organizations/${org.id}/members`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email }),
    });
    const data = await response.json().catch(() => ({}));
    if (response.ok) {
      setMessage({ ok: true, text: data.detail || "Toegevoegd." });
      setEmail("");
      onInvited();
    } else {
      setMessage({ ok: false, text: data.detail || "Uitnodigen is niet gelukt." });
    }
    setBusy(false);
  };

  return (
    <form onSubmit={submit} className="mt-2">
      <div className="flex gap-2">
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="E-mailadres van collega"
          className="flex-1 rounded-md px-3 py-1.5 text-sm bg-white border border-sidebar-menu-border placeholder:text-gray-400"
        />
        <button
          type="submit"
          disabled={busy}
          className="px-3 py-1.5 rounded-md bg-green text-white text-sm hover:bg-dark-green disabled:opacity-50"
        >
          <FontAwesomeIcon icon={faUserPlus} className="pr-1" />
          Uitnodigen
        </button>
      </div>
      {message && (
        <p className={`text-xs mt-1 ${message.ok ? "text-green" : "text-red-600"}`}>
          {message.text}
        </p>
      )}
    </form>
  );
};

const RemoveButton = ({ title, onClick }: { title: string; onClick: () => void }) => (
  <button title={title} onClick={onClick} className="text-gray-300 hover:text-red-600 px-1">
    <FontAwesomeIcon icon={faTrashCan} className="text-xs" />
  </button>
);

const OrganizationCard = ({ org, onChanged }: { org: Organization; onChanged: () => void }) => {
  const remove = async (path: string) => {
    await fetch(`/api/organizations/${org.id}/${path}`, { method: "DELETE" });
    onChanged();
  };
  return (
    <li className="p-4 mb-2 bg-white rounded shadow border-gray-100 border">
      <div className="flex items-center justify-between">
        <span className="font-semibold">{org.name}</span>
        {org.isOwner && (
          <span className="text-xs text-gold-brown" title="Jij bent eigenaar">
            <FontAwesomeIcon icon={faCrown} className="pr-1" />
            eigenaar
          </span>
        )}
      </div>
      <ul className="mt-2 text-sm text-gray-700">
        <li className="py-0.5">
          {org.owner} <span className="text-xs text-gray-400">(eigenaar)</span>
        </li>
        {org.members.map((m) => (
          <li key={m.username} className="py-0.5 flex items-center justify-between group">
            {memberName(m)}
            {org.isOwner && (
              <RemoveButton
                title="Lid verwijderen"
                onClick={() => remove(`members/${encodeURIComponent(m.username)}`)}
              />
            )}
          </li>
        ))}
        {org.invites.map((inv) => (
          <li key={inv.id} className="py-0.5 flex items-center justify-between text-gray-400">
            <span>
              {inv.email} <span className="text-xs">(uitgenodigd — wacht op registratie)</span>
            </span>
            <RemoveButton
              title="Uitnodiging intrekken"
              onClick={() => remove(`invites/${inv.id}`)}
            />
          </li>
        ))}
      </ul>
      {org.isOwner && <InviteForm org={org} onInvited={onChanged} />}
    </li>
  );
};

export const UserPage = () => {
  const authenticated = useAccount((s) => !!s.user);
  const [account, setAccount] = useState<Account | null>(null);

  const refresh = useCallback(() => {
    fetch("/api/account")
      .then((r) => r.json())
      .then(setAccount)
      .catch(() => setAccount(null));
  }, []);
  useEffect(refresh, [refresh]);

  if (!authenticated) return null;
  const user = account?.user;

  return (
    <SideBar>
      <div className="p-6">
        <CloseButton className="text-black hover:text-gray hover:bg-black" />
        <h1 className="text-2xl font-semibold mb-4">Account</h1>
        {user && (
          <dl className="mb-4 text-sm">
            <div className="flex py-1">
              <dt className="w-36 text-gray-500">Naam</dt>
              <dd>
                {user.firstName} {user.lastName}
              </dd>
            </div>
            <div className="flex py-1">
              <dt className="w-36 text-gray-500">Gebruikersnaam</dt>
              <dd>{user.username}</dd>
            </div>
            <div className="flex py-1">
              <dt className="w-36 text-gray-500">E-mail</dt>
              <dd>{user.email}</dd>
            </div>
          </dl>
        )}
        {/* Bestaand (Django) wachtwoordformulier; komt via "Terug naar begin" terug */}
        <a
          href="/auth/password_change/"
          className="inline-block mb-6 px-3 py-1.5 rounded-md bg-bright-yellow text-black text-sm hover:bg-yellow-light hover:text-dark-green"
        >
          <FontAwesomeIcon icon={faKey} className="pr-1" />
          Wachtwoord wijzigen
        </a>

        <h2 className="text-lg font-semibold mb-2">Organisaties</h2>
        {account && !account.organizations.length && (
          <p className="text-sm text-gray-500">Je bent nog geen lid van een organisatie.</p>
        )}
        <ul>
          {account?.organizations.map((org) => (
            <OrganizationCard key={org.id} org={org} onChanged={refresh} />
          ))}
        </ul>
      </div>
    </SideBar>
  );
};
